import { Fragment, useEffect, useMemo, useState, useRef, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, request } from '../api/client';

type Canal = 'evolution' | 'whatsapp_cloud' | 'webchat';

interface ConversaItem {
  id: string;
  clienteNome: string;
  telefone: string;
  pausada: boolean;
  intervencaoHumana: boolean;
  flagIntervencao: string | null;
  ultimaMensagemEm: string | null;
  ticketsAbertos: number;
  validada: boolean;
  creditosTiragem: number;
  canal: Canal;
  ultimaInstancia: string | null;
  whatsappCloudConfigId: string | null;
  cloudDisplayNumber: string | null;
  janela24hExpiraEm: string | null;
  janela24hMinutos: number | null;
}

interface ConversaPage {
  items: ConversaItem[];
  total: number;
  page: number;
  pageSize: number;
}

interface CloudConfig {
  id: string;
  nome: string;
  displayPhoneNumber: string;
  ativo: boolean;
}

interface InstanceInfo {
  name: string;
  connectedNumber: string | null;
  lastConnectedNumber: string | null;
}

interface Anexo {
  tipo: string;
  mimetype: string;
  base64: string;
  caption: string | null;
}

interface Mensagem {
  id: string;
  autor: 'cliente' | 'sara' | 'humano';
  conteudo: string;
  intent: string | null;
  tags: string[];
  enviadaEm: string;
  anexos: unknown;
}

interface CompraInfo {
  produto: string;
  status: string;
  valorCentavos: number;
  compradaEm: string;
}

interface ConversaDetail {
  conversa: {
    id: string;
    pausada: boolean;
    ultimaInstancia: string | null;
    canal: Canal;
    validada: boolean;
  };
  cliente: {
    id: string;
    primeiroNome: string;
    nomeCompleto: string | null;
    telefone: string;
    email: string | null;
    status: string;
    creditosTiragem: number;
    primeiraCompra: string | null;
    acessoMapaManual: boolean;
  };
  compras: CompraInfo[];
  mensagens: Mensagem[];
}

type StatusFilter = 'todos' | 'comMensagem' | 'semResposta' | 'naoVisualizada';

export function ChatsPage(): JSX.Element {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [canalFilter, setCanalFilter] = useState<'todos' | Canal>('todos');
  const [numeroFilter, setNumeroFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('comMensagem');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Carrega listas de instâncias + configs cloud pra montar filtro de números.
  const { data: instances } = useQuery<InstanceInfo[]>({
    queryKey: ['admin-instances'],
    queryFn: () => request<InstanceInfo[]>('/admin/instances'),
    staleTime: 60_000,
  });
  const { data: cloudConfigs } = useQuery<CloudConfig[]>({
    queryKey: ['cloud-configs'],
    queryFn: () => request<CloudConfig[]>('/admin/whatsapp-cloud/configs'),
    staleTime: 60_000,
  });

  // Lista de números disponíveis pra filtrar (instances evo + cloud configs)
  const numeroOptions = useMemo(() => {
    const opts: Array<{ key: string; label: string; canal: Canal }> = [];
    for (const i of instances ?? []) {
      const num = i.connectedNumber ?? i.lastConnectedNumber;
      opts.push({
        key: i.name,
        label: `${i.name}${num ? ` · ${formatPhone(num)}` : ''}`,
        canal: 'evolution',
      });
    }
    for (const c of cloudConfigs ?? []) {
      opts.push({
        key: c.id,
        label: `${c.nome} (cloud) · ${formatPhone(c.displayPhoneNumber)}`,
        canal: 'whatsapp_cloud',
      });
    }
    return opts;
  }, [instances, cloudConfigs]);

  const filteredNumeroOpts = numeroOptions.filter(
    (o) => canalFilter === 'todos' || o.canal === canalFilter,
  );

  // Lista de conversas
  const params = new URLSearchParams();
  if (canalFilter !== 'todos') params.set('canal', canalFilter);
  if (numeroFilter) params.set('numero', numeroFilter);
  if (search) params.set('search', search);
  if (statusFilter === 'comMensagem') params.set('comMensagem', 'true');
  if (statusFilter === 'semResposta') params.set('semResposta', 'true');
  if (statusFilter === 'naoVisualizada') params.set('naoVisualizada', 'true');
  params.set('pageSize', '100');

  const { data, isLoading } = useQuery<ConversaPage>({
    queryKey: ['chats-list', canalFilter, numeroFilter, search, statusFilter],
    queryFn: () => request<ConversaPage>(`/admin/conversas?${params}`),
    refetchInterval: 15_000,
  });

  return (
    <section className="space-y-3 h-[calc(100vh-7rem)]">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="h1">Chats</h1>
          <p className="muted text-xs">
            Lista de conversas estilo WhatsApp Web. Filtra por canal e nº receiver.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-[360px_1fr] gap-3 h-full overflow-hidden">
        {/* COLUNA ESQUERDA — lista */}
        <aside className="card flex flex-col overflow-hidden p-0">
          <div className="p-3 border-b border-sara-border space-y-2">
            <input
              type="search"
              className="input text-sm"
              placeholder="Buscar nome ou telefone…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <div className="flex gap-1 flex-wrap">
              {(['todos', 'evolution', 'whatsapp_cloud', 'webchat'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`text-xs px-2 py-1 rounded-full ${
                    canalFilter === c
                      ? 'bg-sara-accent text-sara-accent-fg'
                      : 'bg-sara-bg border border-sara-border text-sara-text-muted'
                  }`}
                  onClick={() => {
                    setCanalFilter(c);
                    setNumeroFilter('');
                  }}
                >
                  {c === 'todos'
                    ? 'Todos'
                    : c === 'evolution'
                      ? 'Evolution'
                      : c === 'whatsapp_cloud'
                        ? 'Cloud API'
                        : 'Webapp'}
                </button>
              ))}
            </div>
            <div className="flex gap-1 flex-wrap" role="radiogroup" aria-label="Filtrar por status">
              {(
                [
                  { key: 'todos', label: 'Todos' },
                  { key: 'comMensagem', label: 'Com msg' },
                  { key: 'semResposta', label: 'Sem resposta' },
                  { key: 'naoVisualizada', label: 'Não atendida' },
                ] as Array<{ key: StatusFilter; label: string }>
              ).map((f) => (
                <button
                  key={f.key}
                  type="button"
                  role="radio"
                  aria-checked={statusFilter === f.key}
                  className={`text-xs px-2 py-1 rounded-full ${
                    statusFilter === f.key
                      ? 'bg-sara-warning/20 text-sara-warning border border-sara-warning/40'
                      : 'bg-sara-bg border border-sara-border text-sara-text-muted'
                  }`}
                  onClick={() => setStatusFilter(f.key)}
                  title={
                    f.key === 'comMensagem'
                      ? 'Só conversas com pelo menos 1 mensagem'
                      : f.key === 'semResposta'
                        ? 'Última msg é do cliente (Sara/humano não respondeu)'
                        : f.key === 'naoVisualizada'
                          ? 'Cliente mandou mas Sara/humano NUNCA respondeu nessa conversa'
                          : 'Todas as conversas'
                  }
                >
                  {f.label}
                </button>
              ))}
            </div>
            <select
              className="input text-xs"
              value={numeroFilter}
              onChange={(e) => setNumeroFilter(e.target.value)}
            >
              <option value="">Todos os nºs</option>
              {filteredNumeroOpts.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 overflow-y-auto" aria-label="Lista de conversas">
            {isLoading ? (
              <p className="muted text-xs p-3">Carregando…</p>
            ) : !data || data.items.length === 0 ? (
              <p className="muted text-xs p-3 italic">Nenhuma conversa com esse filtro.</p>
            ) : (
              data.items.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`w-full text-left px-3 py-2.5 border-b border-sara-border hover:bg-sara-surface-hover ${
                    selectedId === c.id ? 'bg-sara-accent/10' : ''
                  }`}
                  onClick={() => setSelectedId(c.id)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm text-sara-text truncate">
                      {c.clienteNome || 'amiga'}
                    </span>
                    <span className="text-[10px] text-sara-text-subtle whitespace-nowrap">
                      {c.ultimaMensagemEm
                        ? new Date(c.ultimaMensagemEm).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                          })
                        : '—'}
                    </span>
                  </div>
                  <p className="text-xs text-sara-text-muted truncate">{formatPhone(c.telefone)}</p>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded ${canalBadgeClass(c.canal)}`}
                    >
                      {canalLabel(c.canal)}
                    </span>
                    {c.canal === 'whatsapp_cloud' && c.janela24hMinutos !== null && (
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded ${
                          c.janela24hMinutos > 60
                            ? 'bg-sara-success/15 text-sara-success'
                            : c.janela24hMinutos > 0
                              ? 'bg-sara-warning/15 text-sara-warning'
                              : 'bg-sara-danger/15 text-sara-danger'
                        }`}
                        title={`Janela 24h: ${c.janela24hMinutos > 0 ? `${Math.floor(c.janela24hMinutos / 60)}h${c.janela24hMinutos % 60}m restantes` : 'expirada'}`}
                      >
                        ⏱{formatMinutes(c.janela24hMinutos)}
                      </span>
                    )}
                    {c.pausada && <span className="text-[10px] text-sara-warning">🛑</span>}
                  </div>
                </button>
              ))
            )}
          </div>
          {data && (
            <p className="text-xs text-sara-text-subtle p-2 border-t border-sara-border">
              {data.items.length}/{data.total} conversa(s)
            </p>
          )}
        </aside>

        {/* COLUNA DIREITA — chat */}
        <main className="card overflow-hidden p-0">
          {selectedId ? (
            <ChatPanel conversaId={selectedId} />
          ) : (
            <div className="h-full grid place-items-center text-sara-text-subtle">
              <div className="text-center">
                <p className="text-3xl mb-2">💬</p>
                <p className="text-sm">Selecione uma conversa pra começar</p>
              </div>
            </div>
          )}
        </main>
      </div>
    </section>
  );
}

function ChatPanel({ conversaId }: { conversaId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showSendBar, setShowSendBar] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [text, setText] = useState('');
  const [instancia, setInstancia] = useState('');

  const { data, isLoading } = useQuery<ConversaDetail>({
    queryKey: ['conversa-detail', conversaId],
    queryFn: () => request<ConversaDetail>(`/admin/conversas/${conversaId}`),
    refetchInterval: 8_000,
  });

  const { data: instances } = useQuery<InstanceInfo[]>({
    queryKey: ['admin-instances'],
    queryFn: () => request<InstanceInfo[]>('/admin/instances'),
    staleTime: 60_000,
  });
  const { data: cloudConfigs } = useQuery<CloudConfig[]>({
    queryKey: ['cloud-configs'],
    queryFn: () => request<CloudConfig[]>('/admin/whatsapp-cloud/configs'),
    staleTime: 60_000,
  });

  // `instancia` no estado é "evolution:<name>" OU "cloud:<id>". Default
  // pega o canal da conversa atual.
  useEffect(() => {
    setInstancia(data?.conversa.ultimaInstancia ?? '');
  }, [data?.conversa.ultimaInstancia]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [data?.mensagens.length]);

  const send = useMutation({
    mutationFn: (body: { instancia?: string; cloudConfigId?: string; texto: string }) =>
      request(`/admin/conversas/${conversaId}/send-human`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      setText('');
      setShowSendBar(false);
      void queryClient.invalidateQueries({ queryKey: ['conversa-detail', conversaId] });
    },
  });

  function handleSend(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    // Canal webchat (webapp EN): não precisa de instância — só o texto.
    if (data?.conversa.canal === 'webchat') {
      send.mutate({ texto: t });
      return;
    }
    if (!instancia) return;
    // Prefixo "cloud:" identifica config Cloud, senão é nome Evolution.
    if (instancia.startsWith('cloud:')) {
      send.mutate({ cloudConfigId: instancia.slice('cloud:'.length), texto: t });
    } else {
      send.mutate({ instancia, texto: t });
    }
  }

  if (isLoading || !data) return <div className="p-4 muted text-sm">Carregando…</div>;

  const isWebchat = data.conversa.canal === 'webchat';
  const identidade = isWebchat ? data.cliente.telefone : formatPhone(data.cliente.telefone);

  return (
    <div className="flex flex-col h-full relative">
      <header className="px-4 py-3 border-b border-sara-border flex items-center justify-between">
        <button
          type="button"
          className="text-left hover:opacity-80"
          onClick={() => setShowContact((v) => !v)}
          title="Ver dados do contato"
        >
          <h2 className="font-semibold text-sara-text">
            {data.cliente.primeiroNome || 'amiga'}{' '}
            <span className="text-sara-text-muted text-xs">ⓘ</span>
          </h2>
          <p className="text-xs muted break-all">
            {identidade}
            {data.conversa.ultimaInstancia && ` · via ${data.conversa.ultimaInstancia}`}
          </p>
        </button>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => setShowSendBar((v) => !v)}
        >
          {showSendBar ? '✕ Fechar' : '📞 Responder'}
        </button>
      </header>

      {showContact && (
        <aside className="absolute top-0 right-0 h-full w-72 max-w-[85%] bg-sara-surface border-l border-sara-border shadow-xl z-20 overflow-y-auto">
          <div className="p-4 space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Contato</h3>
              <button
                type="button"
                className="text-sara-text-muted hover:text-sara-text"
                onClick={() => setShowContact(false)}
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>
            <div>
              <p className="font-medium">
                {data.cliente.nomeCompleto || data.cliente.primeiroNome}
              </p>
              <p className="text-xs muted break-all">
                {isWebchat ? '✉️ ' : '📱 '}
                {data.cliente.email || data.cliente.telefone}
              </p>
              {data.cliente.email && !isWebchat && (
                <p className="text-xs muted break-all">✉️ {data.cliente.email}</p>
              )}
            </div>

            <dl className="space-y-1.5">
              <ContactRow label="Canal" value={data.conversa.canal} />
              <ContactRow label="Status" value={data.cliente.status} />
              <ContactRow label="Liberada" value={data.conversa.validada ? 'sim' : 'não'} />
              <ContactRow
                label="Créditos de tiragem"
                value={String(data.cliente.creditosTiragem)}
                highlight={data.cliente.creditosTiragem > 0}
              />
              <ContactRow
                label="1ª compra"
                value={
                  data.cliente.primeiraCompra
                    ? `${dtCurto(data.cliente.primeiraCompra)} (${diasDesde(data.cliente.primeiraCompra)}d)`
                    : '—'
                }
              />
              <ContactRow
                label="Acesso mapa"
                value={data.cliente.acessoMapaManual ? 'manual' : '—'}
              />
            </dl>

            <div>
              <p className="font-medium mb-1">
                Compras ({data.compras.filter((c) => c.status === 'aprovada').length} aprovadas)
              </p>
              {data.compras.length === 0 ? (
                <p className="text-xs muted">Nenhuma compra registrada.</p>
              ) : (
                <ul className="space-y-1">
                  {data.compras.map((c, i) => (
                    <li
                      key={i}
                      className="text-xs flex justify-between gap-2 border-b border-sara-border/40 pb-1"
                    >
                      <span>
                        {c.produto}{' '}
                        <span className={c.status === 'aprovada' ? 'text-sara-success' : 'muted'}>
                          ({c.status})
                        </span>
                      </span>
                      <span className="muted whitespace-nowrap">
                        R$ {(c.valorCentavos / 100).toFixed(2)} · {dtCurto(c.compradaEm)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {data.compras.filter((c) => c.produto === 'tiragem_avulsa' && c.status === 'aprovada')
                .length > 0 && (
                <p className="text-xs text-sara-success mt-1">
                  🃏 comprou{' '}
                  {
                    data.compras.filter(
                      (c) => c.produto === 'tiragem_avulsa' && c.status === 'aprovada',
                    ).length
                  }{' '}
                  tiragem(ns) avulsa(s)
                </p>
              )}
            </div>
          </div>
        </aside>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-sara-bg">
        {data.mensagens.length === 0 ? (
          <p className="text-sara-text-subtle italic text-center mt-8 text-sm">Sem mensagens.</p>
        ) : (
          data.mensagens.map((m, i) => {
            const prev = data.mensagens[i - 1];
            const showDay =
              !prev ||
              new Date(prev.enviadaEm).toDateString() !== new Date(m.enviadaEm).toDateString();
            return (
              <Fragment key={m.id}>
                {showDay && (
                  <div className="flex justify-center my-2">
                    <span className="text-[10px] uppercase tracking-wider text-sara-text-muted bg-sara-surface border border-sara-border rounded-full px-3 py-1">
                      {dayLabelPt(m.enviadaEm)}
                    </span>
                  </div>
                )}
                <MessageBubble msg={m} />
              </Fragment>
            );
          })
        )}
      </div>

      {showSendBar && (
        <form
          onSubmit={handleSend}
          className="p-3 border-t border-sara-border space-y-2 bg-sara-surface"
        >
          {data.conversa.canal === 'webchat' ? (
            <p className="text-xs muted">
              💬 Resposta manual no chat do webapp — a pessoa recebe como se fosse a Sara.
            </p>
          ) : (
            <div className="flex gap-2 items-center text-xs">
              <span className="muted">Por:</span>
              <select
                className="input text-xs"
                value={instancia}
                onChange={(e) => setInstancia(e.target.value)}
              >
                <option value="">Escolha o nº…</option>
                {(instances ?? []).length > 0 && (
                  <optgroup label="Evolution">
                    {(instances ?? []).map((i) => (
                      <option key={i.name} value={i.name}>
                        {i.name} {i.connectedNumber ? `(${i.connectedNumber} ✓)` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
                {(cloudConfigs ?? []).filter((c) => c.ativo).length > 0 && (
                  <optgroup label="WhatsApp Cloud (Oficial)">
                    {(cloudConfigs ?? [])
                      .filter((c) => c.ativo)
                      .map((c) => (
                        <option key={c.id} value={`cloud:${c.id}`}>
                          {c.nome} ({c.displayPhoneNumber})
                        </option>
                      ))}
                  </optgroup>
                )}
              </select>
            </div>
          )}
          <div className="flex gap-2">
            <textarea
              className="input flex-1 min-h-[50px] resize-y text-sm"
              placeholder="Digita aqui…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={send.isPending}
              maxLength={2000}
            />
            <button
              type="submit"
              className="btn-primary self-end"
              disabled={
                send.isPending || !text.trim() || (data.conversa.canal !== 'webchat' && !instancia)
              }
            >
              {send.isPending ? '…' : 'Enviar'}
            </button>
          </div>
          {send.isError && (
            <p className="text-sara-danger text-xs">
              {send.error instanceof ApiError ? send.error.message : 'Erro'}
            </p>
          )}
        </form>
      )}
    </div>
  );
}

function MessageBubble({ msg }: { msg: Mensagem }): JSX.Element {
  const isCliente = msg.autor === 'cliente';
  const isHumano = msg.autor === 'humano';
  const align = isCliente ? 'justify-start' : 'justify-end';
  const bg = isCliente
    ? 'bg-sara-surface border border-sara-border'
    : isHumano
      ? 'bg-amber-500/15 border border-amber-500/40'
      : 'bg-sara-accent text-sara-accent-fg';
  const radius = isCliente ? 'rounded-bl-sm' : 'rounded-br-sm';
  const anexos = parseAnexos(msg.anexos);
  const [translated, setTranslated] = useState<string | null>(null);
  const [loadingT, setLoadingT] = useState(false);

  async function doTranslate(): Promise<void> {
    if (translated !== null) {
      setTranslated(null);
      return;
    }
    setLoadingT(true);
    try {
      const r = await request<{ translated: string }>('/admin/translate', {
        method: 'POST',
        body: { text: msg.conteudo },
      });
      setTranslated(r.translated || '(vazio)');
    } catch {
      setTranslated('(erro ao traduzir)');
    } finally {
      setLoadingT(false);
    }
  }

  return (
    <div className={`flex ${align}`}>
      <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm break-words ${bg} ${radius}`}>
        {isHumano && (
          <p className="text-[10px] font-semibold uppercase tracking-wide opacity-60 mb-0.5">
            você (manual)
          </p>
        )}
        {anexos.map((a, i) => (
          <a
            key={i}
            href={`data:${a.mimetype};base64,${a.base64}`}
            target="_blank"
            rel="noreferrer"
            className="block mb-1"
          >
            <img
              src={`data:${a.mimetype};base64,${a.base64}`}
              alt={a.caption ?? 'imagem enviada pela cliente'}
              className="rounded-lg max-w-full max-h-72 object-contain"
            />
          </a>
        ))}
        <span className="whitespace-pre-wrap">{renderText(msg.conteudo)}</span>
        {translated !== null && (
          <div className="mt-1.5 pt-1.5 border-t border-current/20 text-[13px] italic opacity-90 whitespace-pre-wrap">
            🇧🇷 {translated}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 mt-0.5">
          {msg.conteudo.trim().length > 0 && (
            <button
              type="button"
              onClick={() => void doTranslate()}
              className="text-[10px] underline opacity-50 hover:opacity-100"
              title="Traduzir esta mensagem pra português"
            >
              {loadingT ? '…' : translated !== null ? 'ocultar' : 'ver em PT-BR'}
            </button>
          )}
          <span className="text-[10px] opacity-50">
            {new Date(msg.enviadaEm).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Renderiza *negrito* estilo WhatsApp, preservando o resto. */
function renderText(text: string): JSX.Element[] {
  return text
    .split(/(\*[^*]+\*)/g)
    .map((p, i) =>
      p.startsWith('*') && p.endsWith('*') && p.length > 2 ? (
        <strong key={i}>{p.slice(1, -1)}</strong>
      ) : (
        <span key={i}>{p}</span>
      ),
    );
}

function dayLabelPt(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Hoje';
  if (d.toDateString() === yest.toDateString()) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

/** Lê `Mensagem.anexos` (Json) defensivamente como lista de imagens base64. */
function parseAnexos(raw: unknown): Anexo[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (a): a is Anexo =>
      typeof a === 'object' &&
      a !== null &&
      typeof (a as Anexo).mimetype === 'string' &&
      typeof (a as Anexo).base64 === 'string',
  );
}

function canalLabel(canal: string): string {
  if (canal === 'whatsapp_cloud') return 'Cloud';
  if (canal === 'webchat') return 'Webapp';
  if (canal === 'telegram') return 'Telegram';
  return 'Evolution';
}

function canalBadgeClass(canal: string): string {
  if (canal === 'whatsapp_cloud') return 'bg-blue-500/15 text-blue-500';
  if (canal === 'webchat') return 'bg-violet-500/15 text-violet-400';
  if (canal === 'telegram') return 'bg-sky-500/15 text-sky-400';
  return 'bg-emerald-500/15 text-emerald-500';
}

function ContactRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}): JSX.Element {
  return (
    <div className="flex justify-between gap-2">
      <dt className="muted">{label}</dt>
      <dd className={highlight ? 'text-sara-success font-medium' : ''}>{value}</dd>
    </div>
  );
}

function dtCurto(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

function diasDesde(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

function formatPhone(raw: string): string {
  if (raw.includes('@')) return raw; // webchat: identidade é email
  const d = raw.replace(/\D/g, '');
  if (d.length === 13 && d.startsWith('55'))
    return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 12 && d.startsWith('55'))
    return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`;
  return d;
}

function formatMinutes(m: number): string {
  if (m <= 0) return 'expirou';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h}h${r > 0 ? r + 'm' : ''}`;
}
